import { useRef, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';
import { useIdskModule } from './useIdskModule';
import { cx } from './cx';

type Variant = 'primary' | 'secondary' | 'warning';

const loadButton = () =>
  import('@id-sk/frontend/idsk/components/button/button.js').then(
    (m) => (m as { default: new (el: Element) => { init: () => void } }).default,
  );

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  isStartButton?: boolean;
  preventDoubleClick?: boolean;
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

function variantClass(variant: Variant | undefined) {
  return variant && variant !== 'primary' ? `idsk-button--${variant}` : undefined;
}

/**
 * idsk/components/button. Never rendered with the native `disabled` attribute by the app (validate on submit);
 * the ID-SK button script (double-click prevention, space-key for link buttons) is initialized per element.
 */
export function Button({ variant, isStartButton, preventDoubleClick, className, type = 'button', children, ref, ...rest }: ButtonProps) {
  const local = useRef<HTMLButtonElement | null>(null);
  useIdskModule(local, loadButton);
  return (
    <button
      ref={(el) => {
        local.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      type={type}
      className={cx('idsk-button', variantClass(variant), isStartButton && 'idsk-button--start', className)}
      data-module="idsk-button"
      data-prevent-double-click={preventDoubleClick ? 'true' : undefined}
      {...rest}
    >
      {children}
      {isStartButton && (
        <svg className="idsk-button__start-icon" xmlns="http://www.w3.org/2000/svg" width="17.5" height="19" viewBox="0 0 33 40" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M0 0h13l20 20-20 20H0l20-20z" />
        </svg>
      )}
    </button>
  );
}

export interface ButtonLinkProps {
  to: string;
  id?: string;
  variant?: Variant;
  isStartButton?: boolean;
  className?: string;
  /** router state of the navigation (e.g. `{ focus }` for RouteFocus) */
  state?: unknown;
  children: ReactNode;
}

/** idsk/components/button rendered as a link (`<a role="button">` in the template; a plain link here because it navigates). */
export function ButtonLink({ to, id, variant, isStartButton, className, state, children }: ButtonLinkProps) {
  return (
    <Link
      id={id}
      to={to}
      state={state}
      draggable={false}
      className={cx('idsk-button', variantClass(variant), isStartButton && 'idsk-button--start', className)}
      data-module="idsk-button"
    >
      {children}
      {isStartButton && (
        <svg className="idsk-button__start-icon" xmlns="http://www.w3.org/2000/svg" width="17.5" height="19" viewBox="0 0 33 40" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M0 0h13l20 20-20 20H0l20-20z" />
        </svg>
      )}
    </Link>
  );
}

export function ButtonGroup({ children }: { children: ReactNode }) {
  return <div className="idsk-button-group">{children}</div>;
}
